import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Bell,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowUpCircle,
  RefreshCw,
  Loader2,
  MapPin,
  Package
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_MAP = {
  urgent: { label: "عاجل", class: "bg-red-100 text-red-700" },
  high: { label: "مرتفع", class: "bg-orange-100 text-orange-700" },
  medium: { label: "متوسط", class: "bg-yellow-100 text-yellow-700" },
  low: { label: "منخفض", class: "bg-green-100 text-green-700" }
};

const ALERT_TYPE_MAP = {
  delay: { label: "تأخير", icon: Clock, class: "bg-yellow-500" },
  escalation: { label: "تصعيد", icon: ArrowUpCircle, class: "bg-red-500" },
  supply_delay: { label: "تأخير مستلزمات", icon: Package, class: "bg-purple-500" },
  bi_recall: { label: "استدعاء تعقيم (BI)", icon: ArrowUpCircle, class: "bg-red-600" },
  autoclave_failure: { label: "فشل أوتوكلاف", icon: ArrowUpCircle, class: "bg-orange-600" }
};

const Alerts = () => {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [alertsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/alerts`, { headers }),
        axios.get(`${API}/api/alerts/stats`, { headers }).catch(() => ({ data: null }))
      ]);
      setAlerts(alertsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("خطأ في تحميل التنبيهات");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API}/api/alerts/${alertId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم تأكيد استلام التنبيه");
      fetchData();
    } catch (err) {
      toast.error("خطأ في تحديث التنبيه");
    }
  };

  const filteredAlerts = alerts.filter(a => {
    if (filter === "all") return true;
    if (filter === "unacknowledged") return !a.acknowledged;
    if (filter === "escalation") return a.alert_type === "escalation";
    return a.alert_type === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#4a3fa8]" data-testid="alerts-title">التنبيهات الذكية</h1>
          <p className="text-slate-500 text-sm mt-1">مراقبة الطلبات المتأخرة والتصعيد التلقائي</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2" data-testid="refresh-alerts">
          <RefreshCw className="w-4 h-4" />
          تحديث
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="alerts-stats">
          <Card className="border-r-4 border-r-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Bell className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">إجمالي التنبيهات</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="total-alerts-count">{stats.total_alerts}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-r-4 border-r-red-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">غير مؤكدة</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="unack-count">{stats.unacknowledged}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-r-4 border-r-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <ArrowUpCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">تم تصعيدها</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="escalated-count">{stats.escalated}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-r-4 border-r-[#655dc6]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-5 h-5 text-[#655dc6]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">حدود التأخير</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">عاجل: {stats.thresholds.urgent}س</span>
                    <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">مرتفع: {stats.thresholds.high}س</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="alerts-filters">
        {[
          { value: "all", label: "الكل", count: alerts.length },
          { value: "unacknowledged", label: "غير مؤكدة", count: alerts.filter(a => !a.acknowledged).length },
          { value: "escalation", label: "تصعيد", count: alerts.filter(a => a.alert_type === "escalation").length },
          { value: "delay", label: "تأخير صيانة", count: alerts.filter(a => a.alert_type === "delay").length },
          { value: "supply_delay", label: "تأخير مستلزمات", count: alerts.filter(a => a.alert_type === "supply_delay").length },
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              filter === f.value
                ? "bg-[#655dc6] text-white shadow-sm"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`filter-${f.value}`}
          >
            <span>{f.label}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              filter === f.value ? "bg-white/20" : "bg-slate-200"
            }`}>{f.count}</span>
          </button>
        ))}
      </div>

      {/* Alerts List */}
      <div className="space-y-3" data-testid="alerts-list">
        {filteredAlerts.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-slate-500 text-lg">لا توجد تنبيهات</p>
            </CardContent>
          </Card>
        ) : (
          filteredAlerts.map(alert => {
            const alertType = ALERT_TYPE_MAP[alert.alert_type] || ALERT_TYPE_MAP.delay;
            const priority = PRIORITY_MAP[alert.priority] || PRIORITY_MAP.medium;
            const AlertIcon = alertType.icon;
            
            return (
              <Card
                key={alert.id}
                className={`transition-all hover:shadow-md ${alert.acknowledged ? "opacity-60" : "border-r-4 border-r-yellow-500"}`}
                data-testid={`alert-${alert.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-1.5 rounded-lg ${alertType.class}`}>
                          <AlertIcon className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-semibold text-slate-800">{alertType.label}</span>
                        <Badge className={`${priority.class} hover:${priority.class}`}>{priority.label}</Badge>
                        {alert.acknowledged && (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">مؤكد</Badge>
                        )}
                      </div>
                      <p className="text-slate-700 mb-1">{alert.request_description}</p>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {alert.location_name || "غير محدد"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          متأخر {alert.hours_elapsed} ساعة
                        </span>
                        {alert.request_status && (
                          <Badge variant="outline" className="text-xs">{alert.request_status}</Badge>
                        )}
                      </div>
                      {alert.acknowledged_by && (
                        <p className="text-xs text-green-600 mt-2">
                          تم التأكيد بواسطة: {alert.acknowledged_by}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center">
                      {!alert.acknowledged && (
                        <Button
                          onClick={() => acknowledgeAlert(alert.id)}
                          className="bg-[#4a3fa8] hover:bg-[#3d3291] text-white"
                          data-testid={`ack-${alert.id}`}
                        >
                          <CheckCircle className="w-4 h-4 ml-2" />
                          تأكيد الاستلام
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Alerts;
